import { FieldErrors, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Box, Grid, Typography } from '@mui/material'
import { zodResolver } from '@hookform/resolvers/zod'
import React, { MutableRefObject, useEffect } from 'react'
import Label from 'components/Label'
import FormInput from 'components/FormInput'
import { useOutletContext } from 'react-router'
import { useAuthContext } from 'contexts/auth.context'
import { Funcionalidades } from 'utils/helpers/funcionalidades'
import {
  SocioeducationMeasureMeasuresAppliedDefaultValues,
  SocioeducationMeasureMeasuresAppliedSchema,
  SocioeducationMeasureMeasuresAppliedType,
} from 'types/socioeducation-measure-measures-applied.type'
import { MeasuresAppliedList } from 'pages/SocioeducationMeasureEdit/MeasuresApplied/components/MeasuresAppliedList'
import FormControlRadio, { RadioOption } from 'components/FormControlRadio'
import { FormInputAutoComplete } from 'components/FormInputAutoComplete'
import { ItemOption } from 'components/InputSelect'
import { useParams } from 'react-router-dom'
import { useMeioAbertoMedidasAplicadas } from 'hooks/meio-aberto-medidas-aplicadas.hook'
import { useCreasUsuario } from 'pages/SearchCreas/hook/creas-usuario.hook'
import { CreasResponse } from 'types/creas.type'
import { addressInit } from 'types/address.type'

export const periodicidadeAcompanhamentoOptions: RadioOption[] = [
  {
    label: 'Semanal',
    value: 'SEMANAL',
  },
  {
    label: 'Quinzenal',
    value: 'QUINZENAL',
  },
]

export const simNaoOptions = [
  {
    label: 'Sim',
    value: 'S',
  },
  {
    label: 'Não',
    value: 'N',
  },
]

export const sugestaoExtincaoOptions: RadioOption[] = [
  {
    label: 'Inserção em Jovem Aprendiz',
    value: 'INSERCAO_JOVEM_APRENDIZ',
  },
  {
    label: 'Inserção em Cursos Profissionalizantes',
    value: 'INSERCAO_CURSOS_PROFISSIONALIZANTES',
  },
  {
    label: 'Questões de Saúde',
    value: 'QUESTAO_SAUDE',
  },
  {
    label: 'Cumprimento do Serviço Militar',
    value: 'CUMPRIMENTO_SERVICO_MILITAR',
  },
  {
    label: 'Inserção no Mercado Formal ou Informal de Trabalho para os maiores de 18 anos',
    value: 'INSERCAO_MERCADO_TRABALHO',
  },
  {
    label: 'Rotina Escolar',
    value: 'ROTINA_ESCOLAR',
  },
  {
    label: 'Outros',
    value: 'OUTROS',
  },
]

export const atividadesOptions: ItemOption[] = [
  {
    label: 'Atuação em atividades administrativas (CRAS, CREAS, CONSELHOS TUTELARES etc.)',
    value: 'ATIVIDADES_ADMINISTRATIVAS',
  },
  {
    label: 'Atuação em Espaços culturais',
    value: 'ESPACOS_CULTURAIS',
  },
  {
    label: 'Atuação em Hospitais ou Unidades Básicas de Saúde',
    value: 'HOSPITAIS_UNIDADES_SAUDE',
  },
  {
    label: 'Atuação em Serviços de limpeza e Manutenção',
    value: 'SERVICOS_LIMPEZA_MANUTENCAO',
  },
  {
    label: 'Atuação em Projetos de Esporte',
    value: 'PROJETOS_ESPORTE',
  },
  {
    label: 'Atuação em Unidades Escolares',
    value: 'UNIDADES_ESCOLARES',
  },
  {
    label: 'Atuação em Projetos Sociais Educacionais',
    value: 'PROJETOS_SOCIAIS_EDUCACIONAIS',
  },
  {
    label: 'Atuação em Unidades de Acolhimento para idosos',
    value: 'UNIDADES_ACOLHIMENTO_IDOSOS',
  },
  {
    label: 'Atuação na área da Defensoria Pública/ Fórum',
    value: 'DEFENSORIA_PUBLICA_FORUM',
  },
  {
    label: 'Atuação em Projetos Sociais de ONGs',
    value: 'PROJETOS_SOCIAIS_ONGS',
  },
  {
    label: 'Atuação no sistema S (SESC, SENAI, SESI etc.)',
    value: 'SISTEMA_S',
  },
]

export const dificuldadeAdesaoOptions: ItemOption[] = [
  {
    label: '1ª Vez',
    value: 'PRIMEIRA_VEZ',
  },
  {
    label: '2ª Vez',
    value: 'SEGUNDA_VEZ',
  },
  {
    label: '3ª Vez',
    value: 'TERCEIRA_VEZ',
  },
]

type OutletContextType = {
  currentStep: number
  formRefs: MutableRefObject<Record<number, { submit: () => Promise<void> | void }>>
}

export default function MeasuresAppliedPage() {
  const { id } = useParams()
  const { currentStep, formRefs } = useOutletContext<OutletContextType>()
  const { handleHasPermission } = useAuthContext()

  const { medidasAplicadas, handleSalvar: handleSalvarMedidasAplicadas } =
    useMeioAbertoMedidasAplicadas(id)

  const form = useForm<SocioeducationMeasureMeasuresAppliedType>({
    resolver: zodResolver(SocioeducationMeasureMeasuresAppliedSchema),
    defaultValues: SocioeducationMeasureMeasuresAppliedDefaultValues,
  })

  const {
    control,
    handleSubmit,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { isDirty, dirtyFields },
  } = form

  const hasDirtyFields = Object.keys(dirtyFields).length > 0 && dirtyFields.constructor === Object

  const isLa = watch('la.ativo')
  const isPsc = watch('psc.ativo')

  const hasEditPermission = handleHasPermission(Funcionalidades.meioAberto.editar)
  const isAllowedByType = isLa || isPsc

  const doNotHasEditPermission = !hasEditPermission || !isAllowedByType

  const handleSalvar = async (data: SocioeducationMeasureMeasuresAppliedType) => {
    console.log('data', data)
    await handleSalvarMedidasAplicadas(data)
  }

  const onError = (errors: FieldErrors<SocioeducationMeasureMeasuresAppliedType>) => {
    const recursiveSetError = (errors: any, parentPath = '') => {
      clearErrors()
      Object.entries(errors).forEach(([key, error]: any) => {
        const path = parentPath ? `${parentPath}.${key}` : key

        if (error?.ref) {
          // Se houver uma referência (ref), usamos o nome da referência para definir o erro
          setError(error.ref.name, { type: error.type, message: error.message })
        } else if (typeof error === 'object') {
          // Recursivamente percorre os objetos internos
          recursiveSetError(error, path)
        }
      })
    }

    recursiveSetError(errors)
    return Promise.reject(errors)
  }

  useEffect(() => {
    if (medidasAplicadas) {
      reset(medidasAplicadas)
    }
  }, [medidasAplicadas])

  formRefs.current[currentStep] = {
    submit: async () => {
      if (isDirty && hasDirtyFields && handleHasPermission(Funcionalidades.meioAberto.editar)) {
        await handleSubmit(handleSalvar, onError)()
      }
      return Promise.resolve()
    },
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(handleSalvar, onError)}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            <Typography variant={'body1'} color={'secondary'}>
              <b>MEDIDAS APLICADAS</b>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <MeasuresAppliedList disabled={doNotHasEditPermission} />
          </Grid>
          {isLa && <LaMeasuresApplied />}
          {isPsc && <PscMeasuresApplied />}
          <Grid item xs={12}>
            <Box display={'flex'} flexDirection={'column'} gap={1}>
              <Label text={'Observações:'} sx={{ fontSize: 14, color: 'text.primary' }} />
              <FormInput
                name={'observacoes'}
                control={control}
                multiline
                minRows={4}
                disabled={doNotHasEditPermission}
              />
            </Box>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}

interface LaMeasuresAppliedProps {
  disabled?: boolean
}

function LaMeasuresApplied({ disabled }: LaMeasuresAppliedProps) {
  const { control } = useFormContext<SocioeducationMeasureMeasuresAppliedType>()

  return (
    <Grid container item xs={12} spacing={1}>
      <Grid item xs={12}>
        <Typography variant={'body1'}>
          <b>Informações Sobre Acompanhamento Da Medida Liberdade Assistida</b>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormControlRadio
          name={'la.periodicidadeAcompanhamento'}
          label={'Periodicidade do Acompanhamento:'}
          control={control}
          options={periodicidadeAcompanhamentoOptions}
        />
      </Grid>
      <Grid item xs={12}>
        <Box display={'flex'} flexDirection={'column'} gap={1}>
          <Label
            text={
              'Atividades Individuais ou Coletivas apresentadas ou desenvolvidas com o Adolescente/Jovem pela equipe de Pedagogia:'
            }
            sx={{ fontSize: 14, color: 'text.primary' }}
          />
          <FormInput
            name={'la.atividadesIndividuaisColetivas'}
            control={control}
            multiline
            minRows={4}
            disabled={disabled}
          />
        </Box>
      </Grid>
    </Grid>
  )
}

interface PscMeasuresAppliedProps {
  disabled?: boolean
}

function PscMeasuresApplied({ disabled }: PscMeasuresAppliedProps) {
  const { creas } = useCreasUsuario()
  const { control, watch, setValue } = useFormContext<SocioeducationMeasureMeasuresAppliedType>()

  const aguardandoArticulacao = watch('psc.aguardandoArticulacao')
  const creasWatch = watch('psc.localCumprimento')

  const isArticulacao = aguardandoArticulacao === 'S'

  useEffect(() => {
    if (!isArticulacao) {
      setValue('psc.localCumprimento', null)
      setValue('psc.profissionalReferencia', null)
      setValue('psc.cargaHoraria', null)
      setValue('psc.tempoDeterminado', null)
      setValue('psc.principaisAtividades', null)
    }
  }, [isArticulacao])

  return (
    <Grid container item xs={12} spacing={2}>
      <Grid item xs={12}>
        <Typography variant={'body1'}>
          <b>Informações Sobre Acompanhamento da Medida Prestação de Serviços à Comunidade</b>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box display={'flex'} flexDirection={'column'} gap={1}>
          <Label
            text={'Habilidades e Potencialidades destacadas pelo adolescente:'}
            sx={{ fontSize: 14, color: 'text.primary' }}
          />
          <FormInput
            name={'psc.habilidadePotencialidades'}
            control={control}
            multiline
            minRows={4}
            disabled={disabled}
          />
        </Box>
      </Grid>
      <Grid container item xs={12} spacing={1}>
        <Grid item md={3} xs={12}>
          <FormControlRadio
            name={'psc.aguardandoArticulacao'}
            label={'Aguardando Articulação:'}
            control={control}
            options={simNaoOptions}
          />
        </Grid>
        {isArticulacao && (
          <>
            <Grid item md={5} xs={12} alignContent={'center'}>
              <FormInputAutoComplete
                label={'Local do Cumprimento:'}
                name={'psc.localCumprimento'}
                control={control}
                options={creas}
                disabled={disabled}
                getOptionLabel={(option) => option.nome}
                getOptionById={() =>
                  creasWatch && creasWatch.id && creasWatch.nome
                    ? ({
                        id: creasWatch.id,
                        nome: creasWatch.nome,
                        abrangencia: [],
                        ativo: '',
                        diretor: '',
                        email: '',
                        endereco: addressInit,
                        telefones: [],
                      } as CreasResponse)
                    : null
                }
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChangeValue={(option) => option || null}
              />
            </Grid>
            <Grid item md={4} xs={12} alignContent={'center'}>
              <FormInput
                name={'psc.profissionalReferencia'}
                label={'Profissional de Referência:'}
                control={control}
                maxLength={100}
              />
            </Grid>
            <Grid item md={3} xs={12} alignContent={'center'}>
              <FormInput
                name={'psc.cargaHoraria'}
                label={'Carga Horária:'}
                control={control}
                maxLength={20}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <FormInput
                name={'psc.tempoDeterminado'}
                label={'Tempo Determinado:'}
                control={control}
                maxLength={60}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FormInputAutoComplete
                name={'psc.principaisAtividades'}
                control={control}
                label={'Principais Atividades:'}
                options={atividadesOptions}
                getOptionLabel={(option) => option.label}
                getOptionById={(id: string | null, options: ItemOption[]) =>
                  options.find((option) => option.value === id) || null
                }
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                onChangeValue={(option) => option?.value || null}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid item xs={12}>
        <FormControlRadio
          name={'psc.sugestaoExtincao'}
          label={'Sugestão de Extinção da PSC antes do término seu cumprimento em razão de:'}
          control={control}
          options={sugestaoExtincaoOptions}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlRadio
          name={'psc.dificuldadeAdesao'}
          label={'Nos casos de dificuldade de adesão – Novo Encaminhamento:'}
          control={control}
          options={dificuldadeAdesaoOptions}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlRadio
          name={'psc.recusaAdolescenteCumprimento'}
          label={'Recusa do Adolescente no Cumprimento da PSC:'}
          control={control}
          options={simNaoOptions}
        />
      </Grid>
    </Grid>
  )
}
